import { useCallback, useEffect, useState } from 'react'
import { dataExportAPI } from '../../services/dataExport'
import { useToast } from '../../components/common/ToastContainer'
import {
  HiOutlineArchiveBoxArrowDown,
  HiOutlineArrowDownTray,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineArrowPath,
} from 'react-icons/hi2'

/**
 * Data Export tab inside ProfileSettingsPage.
 *
 * Lets a user request a ZIP archive of their workspace data under GDPR
 * Article 20. Shows status and history of past requests.
 *
 * Auto-polls every 5 s while a request is non-terminal.
 */

const POLL_MS = 5000

const DataExportTab = ({ isOwner = true }) => {
  const toast = useToast()
  const [exports, setExports] = useState([])
  const [loading, setLoading] = useState(true)
  const [requesting, setRequesting] = useState(false)
  const [includeFiles, setIncludeFiles] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const res = await dataExportAPI.list()
      if (res?.success) {
        setExports(res.data?.data ?? res.data ?? [])
      }
    } catch (e) { /* non-fatal */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  // Poll while any export is non-terminal
  useEffect(() => {
    const anyActive = exports.some(e => !e.is_terminal)
    if (!anyActive) return undefined
    const t = setInterval(refresh, POLL_MS)
    return () => clearInterval(t)
  }, [exports, refresh])

  const handleRequest = async () => {
    if (!isOwner) return
    setRequesting(true)
    try {
      const res = await dataExportAPI.request({ scope: 'account', include_files: includeFiles })
      if (res?.success) {
        toast?.success?.('Export queued. We will email you when it is ready.', 'Data export')
        await refresh()
      } else {
        toast?.error?.(res?.data?.message || res?.error?.message || 'Could not start export.', 'Data export')
      }
    } catch (e) {
      toast?.error?.('Could not start export.', 'Data export')
    } finally {
      setRequesting(false)
    }
  }

  const handleDownload = async (token) => {
    toast?.info?.('Preparing download…', 'Data export')
    const res = await dataExportAPI.download(token)
    if (!res.success) {
      toast?.error?.(res.error || 'Download failed.', 'Data export')
    } else {
      // Refresh so download_count + downloaded_at update visually
      refresh()
    }
  }

  const activeExport = exports.find(e => !e.is_terminal)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Data export</h2>
        <p className="text-sm text-gray-600 mt-1">
          Download a copy of all your workspace data, including documents,
          messages, clients, cases, and activity logs. Files come as a single ZIP.
          This implements GDPR Article 20 (right to data portability).
        </p>
      </div>

      {/* Request panel */}
      {!isOwner ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-900">
          Only the workspace owner can export workspace data. Need a copy of just your own
          notifications and profile? Contact <a className="underline" href="mailto:info@ovacs.com">info@ovacs.com</a>.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Request a new export</h3>
            <p className="text-sm text-gray-600 mt-1">
              The archive is built in the background. You'll receive an email with a
              download link when it's ready — usually within a few minutes for small
              workspaces, longer for large ones.
            </p>
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeFiles}
              onChange={(e) => setIncludeFiles(e.target.checked)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-800">Include file contents</div>
              <div className="text-gray-500 text-xs">
                Bundles the actual bytes of every document, voice and text message
                (from local + cloud storage). Individual files larger than 100 MB are
                skipped to keep the ZIP manageable. Uncheck for a smaller metadata-only
                export.
              </div>
            </div>
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRequest}
              disabled={!!activeExport || requesting}
              className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
            >
              <HiOutlineArchiveBoxArrowDown className="w-4 h-4" />
              {requesting ? 'Queuing…' : activeExport ? 'An export is in progress…' : 'Request export'}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              title="Refresh"
              className="p-2 rounded border border-gray-300 hover:bg-gray-50"
            >
              <HiOutlineArrowPath className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="text-xs text-gray-500 border-t border-gray-100 pt-3">
            Archives are available to download for <strong>48 hours</strong>, after which
            they are automatically deleted from our servers. You can always request a new one.
          </div>
        </div>
      )}

      {/* History */}
      <div className="border border-gray-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Past requests</h3>
          <span className="text-xs text-gray-500">{exports.length} total</span>
        </div>
        {loading && exports.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : exports.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            You haven't requested any data exports yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {exports.map((e) => (
              <ExportRow key={e.id} export={e} onDownload={handleDownload} />
            ))}
          </div>
        )}
      </div>

      {/* GDPR footer */}
      <div className="border-l-4 border-brand bg-info-soft p-4 rounded text-xs text-blue-900 leading-relaxed">
        Under the EU General Data Protection Regulation (GDPR), you have the right to receive
        your personal data in a structured, commonly used, machine-readable format. The exported
        ZIP includes a <code className="bg-white px-1 rounded">README.txt</code> describing every
        section. If anything is missing or incorrect, contact
        <a className="underline ml-1" href="mailto:info@ovacs.com">info@ovacs.com</a>.
      </div>
    </div>
  )
}

const STATUS_META = {
  queued:    { label: 'Queued',    color: 'bg-gray-100 text-gray-700',    icon: HiOutlineClock },
  running:   { label: 'Building',  color: 'bg-info-soft text-info',    icon: HiOutlineArrowPath },
  completed: { label: 'Ready',     color: 'bg-green-100 text-green-800',  icon: HiOutlineCheckCircle },
  failed:    { label: 'Failed',    color: 'bg-red-100 text-red-800',      icon: HiOutlineXCircle },
  expired:   { label: 'Expired',   color: 'bg-gray-200 text-gray-700',    icon: HiOutlineClock },
}

const ExportRow = ({ export: e, onDownload }) => {
  const meta = STATUS_META[e.status] || STATUS_META.queued
  const Icon = meta.icon
  return (
    <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Icon className={`w-5 h-5 mt-0.5 ${e.status === 'running' ? 'animate-spin' : ''} ${
          e.status === 'completed' ? 'text-success'
          : e.status === 'failed' ? 'text-danger'
          : 'text-gray-500'
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase font-semibold tracking-wide px-2 py-0.5 rounded ${meta.color}`}>
              {meta.label}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {e.account?.name || 'Personal data'}
            </span>
            {e.scope === 'user' && <span className="text-xs text-gray-500">(user scope)</span>}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Requested {formatRelative(e.created_at)}
            {e.status === 'completed' && e.archive_size_mb > 0 && (
              <> · {e.archive_size_mb} MB · {e.total_documents} document{e.total_documents === 1 ? '' : 's'}</>
            )}
            {e.status === 'failed' && e.last_error && (
              <> · <span className="text-danger">{e.last_error}</span></>
            )}
            {e.expires_at && e.status === 'completed' && (
              <> · expires {new Date(e.expires_at).toLocaleString()}</>
            )}
          </div>
        </div>
      </div>
      <div className="flex-shrink-0">
        {e.is_downloadable ? (
          <button
            type="button"
            onClick={() => onDownload(e.request_token)}
            className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5"
          >
            <HiOutlineArrowDownTray className="w-4 h-4" />
            Download
            {e.download_count > 0 && (
              <span className="text-xs bg-blue-700 px-1.5 py-0.5 rounded">×{e.download_count}</span>
            )}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function formatRelative(iso) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.max(0, Math.floor((now - then) / 1000))
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(iso).toLocaleString()
}

export default DataExportTab
